import { create } from 'zustand';
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  Unsubscribe,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';

const VERIFICATION_RESULT_NOTIFICATION_TYPES = [
  'verification_review_approved',
  'verification_review_rejected',
  'verification_review_remanded',
] as const;

const MAX_BATCH_WRITES = 450;

interface NotificationState {
  unreadVerificationResultCount: number;
  notificationUnsubscribe: Unsubscribe | null;
  subscribeToNotifications: (userId: string) => void;
  unsubscribeFromNotifications: () => void;
  markVerificationNotificationsAsRead: (userId: string) => Promise<void>;
  reset: () => void;
}

const createVerificationResultQuery = (userId: string) =>
  query(
    collection(db, 'notifications', userId, 'logs'),
    where('type', 'in', VERIFICATION_RESULT_NOTIFICATION_TYPES)
  );

export const useNotificationStore = create<NotificationState>((set, get) => ({
  unreadVerificationResultCount: 0,
  notificationUnsubscribe: null,

  subscribeToNotifications: (userId: string) => {
    get().unsubscribeFromNotifications();

    if (!userId) {
      set({ unreadVerificationResultCount: 0 });
      return;
    }

    const unsubscribe = onSnapshot(
      createVerificationResultQuery(userId),
      (snapshot) => {
        const unreadCount = snapshot.docs.reduce((count, logDoc) => {
          const readAt = logDoc.data().readAt;
          return readAt === undefined || readAt === null ? count + 1 : count;
        }, 0);
        set({ unreadVerificationResultCount: unreadCount });
      },
      (error) => {
        console.error('Failed to subscribe verification notifications:', error);
      }
    );

    set({ notificationUnsubscribe: unsubscribe });
  },

  unsubscribeFromNotifications: () => {
    const unsubscribe = get().notificationUnsubscribe;
    if (unsubscribe) {
      unsubscribe();
    }
    set({ notificationUnsubscribe: null, unreadVerificationResultCount: 0 });
  },

  markVerificationNotificationsAsRead: async (userId: string) => {
    if (!userId) {
      return;
    }

    try {
      const snapshot = await getDocs(createVerificationResultQuery(userId));
      const unreadDocs = snapshot.docs.filter((logDoc) => {
        const readAt = logDoc.data().readAt;
        return readAt === undefined || readAt === null;
      });

      if (unreadDocs.length === 0) {
        return;
      }

      for (let startIndex = 0; startIndex < unreadDocs.length; startIndex += MAX_BATCH_WRITES) {
        const batch = writeBatch(db);
        const chunk = unreadDocs.slice(startIndex, startIndex + MAX_BATCH_WRITES);
        chunk.forEach((logDoc) => {
          batch.update(
            doc(db, 'notifications', userId, 'logs', logDoc.id),
            { readAt: serverTimestamp() }
          );
        });
        await batch.commit();
      }
    } catch (error) {
      console.error('Failed to mark verification notifications as read:', error);
    }
  },

  reset: () => {
    get().unsubscribeFromNotifications();
    set({ unreadVerificationResultCount: 0 });
  },
}));
